/**
 * NUHO Theme Animations & Interactivity
 * - Scroll-triggered fade/slide animations using Intersection Observer
 * - Parallax effects for background images
 * - Preloader word animation
 * - Science modal open/close + scroll lock
 * - Video play/pause toggle
 * - Language selector dropdown
 */

(function() {
  'use strict';

  // ── Configuration ──
  const ANIMATION_CONFIG = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };
  const PARALLAX_SPEED = 0.3;

  // ── Scroll-triggered animations ──
  function initScrollAnimations() {
    var animatedElements = document.querySelectorAll('[data-animate]');
    if (!animatedElements.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = el.dataset.animateDelay || 0;
          setTimeout(function() {
            el.classList.add('animated');
          }, delay);
          observer.unobserve(el);
        }
      });
    }, ANIMATION_CONFIG);

    animatedElements.forEach(function(el) { observer.observe(el); });

    // Safety net: anything still un-revealed after 2.5s gets force-revealed
    // (prevents permanently-invisible content if observer misses or page is fully visible at load)
    setTimeout(function() {
      document.querySelectorAll('[data-animate]:not(.animated)').forEach(function(el) {
        el.classList.add('animated');
      });
    }, 2500);
  }

  // ── Parallax effect ──
  function initParallax() {
    var parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    var ticking = false;

    function updateParallax() {
      var scrollY = window.scrollY;
      parallaxElements.forEach(function(el) {
        var speed = parseFloat(el.dataset.parallax) || PARALLAX_SPEED;
        var rect = el.getBoundingClientRect();
        var elementTop = rect.top + scrollY;
        var viewportHeight = window.innerHeight;
        if (scrollY + viewportHeight > elementTop && scrollY < elementTop + rect.height) {
          var yOffset = (scrollY - elementTop + viewportHeight) * speed;
          el.style.transform = 'translate3d(0, ' + yOffset + 'px, 0)';
        }
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    updateParallax();
  }

  // ── Stagger animations for child elements ──
  function initStaggerAnimations() {
    var staggerContainers = document.querySelectorAll('[data-stagger]');
    staggerContainers.forEach(function(container) {
      var children = container.querySelectorAll('[data-stagger-item]');
      var baseDelay = parseInt(container.dataset.stagger) || 100;
      children.forEach(function(child, index) {
        child.dataset.animateDelay = index * baseDelay;
        child.setAttribute('data-animate', child.dataset.staggerItem || 'fade-up');
      });
    });
  }

  // ── Section reveal ──
  function initSectionReveal() {
    var sections = document.querySelectorAll('.shopify-section');
    sections.forEach(function(section, index) {
      if (section.closest('[id*="header"]') || section.closest('[id*="footer"]')) return;
      // Skip content-rich list sections — they have many cards that would all
      // flash invisible on slow IntersectionObserver runs (blog list, journal block, articles).
      if (section.querySelector('.blog-list, .jblock, .kpb__articles, .art__body')) return;
      if (!section.hasAttribute('data-animate') && !section.querySelector('[data-animate]')) {
        section.setAttribute('data-animate', 'fade-up');
        section.setAttribute('data-animate-delay', index * 50);
      }
    });
  }

  // ── Smooth scroll for anchor links ──
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
      anchor.addEventListener('click', function(e) {
        var targetId = this.getAttribute('href');
        if (targetId === '#') return;
        var target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // ── Counter animation ──
  function initCounterAnimation() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var target = parseInt(el.dataset.counter);
          var duration = parseInt(el.dataset.counterDuration) || 2000;
          var suffix = el.dataset.counterSuffix || '';
          animateCounter(el, target, duration, suffix);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(function(el) { observer.observe(el); });
  }

  function animateCounter(el, target, duration, suffix) {
    var startTime = performance.now();
    function update(currentTime) {
      var elapsed = currentTime - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var easeProgress = 1 - Math.pow(1 - progress, 3);
      var current = Math.floor(easeProgress * target);
      el.textContent = current + suffix;
      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        el.textContent = target + suffix;
      }
    }
    requestAnimationFrame(update);
  }

  // ── Language Selector Dropdown ──
  function initLanguageSelector() {
    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('[data-lang-toggle]');
      var dropdown = document.querySelector('[data-lang-dropdown]');
      if (!dropdown) return;

      if (trigger) {
        var isOpen = dropdown.style.display === 'block';
        dropdown.style.display = isOpen ? 'none' : 'block';
        var chevron = trigger.querySelector('.lang-chevron');
        if (chevron) {
          chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
        }
        return;
      }

      // Close on outside click
      if (!e.target.closest('[data-lang-dropdown]') && !e.target.closest('[data-lang-toggle]')) {
        dropdown.style.display = 'none';
        var toggleBtn = document.querySelector('[data-lang-toggle]');
        if (toggleBtn) {
          var chev = toggleBtn.querySelector('.lang-chevron');
          if (chev) chev.style.transform = '';
        }
      }
    });
  }

  // ── Preloader ──
  function initPreloader() {
    var preloader = document.querySelector('.preloader');
    if (!preloader) return;

    // Only show on first visit per session (not internal navigation)
    if (sessionStorage.getItem('nuho-preloader-shown')) {
      preloader.remove();
      return;
    }

    var words = preloader.querySelectorAll('.preloader__word');
    if (!words.length) return;

    var dismissed = false;

    function dismissPreloader() {
      if (dismissed) return;
      dismissed = true;
      sessionStorage.setItem('nuho-preloader-shown', '1');
      preloader.classList.add('fade-out');
      setTimeout(function() {
        preloader.remove();
      }, 600);
    }

    // Click anywhere to skip preloader
    preloader.addEventListener('click', dismissPreloader);

    function showWord(i) {
      if (dismissed) return;

      // Exit previous
      if (i > 0 && words[i - 1]) {
        words[i - 1].classList.remove('active');
        words[i - 1].classList.add('exit');
      }

      // Show current
      if (words[i]) {
        words[i].classList.add('active');
      }

      // Schedule next
      if (i < words.length - 1) {
        var delay = (i === 0) ? 1200 : 1400;
        setTimeout(function() {
          showWord(i + 1);
        }, delay);
      } else {
        // Last word shown — fade out preloader
        setTimeout(dismissPreloader, 1000);
      }
    }

    // Start after a small delay
    setTimeout(function() {
      showWord(0);
    }, 100);
  }

  // ── Science Modal ──
  function initScienceModal() {
    document.addEventListener('click', function(e) {
      // Open modal
      var nutrientRow = e.target.closest('[data-nutrient]');
      if (nutrientRow) {
        var key = nutrientRow.dataset.nutrient;
        var modal = document.querySelector('[data-science-modal]');
        if (!modal) return;

        // Populate modal with nutrient data
        var data = document.querySelector('[data-nutrient-data="' + key + '"]');
        if (data) {
          var content = modal.querySelector('.science-modal__content');
          if (content) {
            content.innerHTML = data.innerHTML;
          }
        }

        modal.classList.add('is-open');
        document.documentElement.classList.add('scroll-lock');
        return;
      }

      // Close modal
      var closeBtn = e.target.closest('[data-close-modal]');
      var overlay = e.target.closest('[data-modal-overlay]');
      if (closeBtn || (overlay && !e.target.closest('.science-modal__panel'))) {
        var openModal = document.querySelector('[data-science-modal].is-open');
        if (openModal) {
          openModal.classList.remove('is-open');
          document.documentElement.classList.remove('scroll-lock');
        }
      }
    });

    // Close on Escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        var openModal = document.querySelector('[data-science-modal].is-open');
        if (openModal) {
          openModal.classList.remove('is-open');
          document.documentElement.classList.remove('scroll-lock');
        }
      }
    });
  }

  // ── Video Play/Pause Toggle ──
  function initVideoToggle() {
    document.addEventListener('click', function(e) {
      var playBtn = e.target.closest('.video-play-btn');
      if (!playBtn) return;

      var container = playBtn.closest('.video-container');
      if (!container) return;

      var video = container.querySelector('video');
      if (!video) return;

      if (video.paused) {
        video.play();
        playBtn.classList.add('is-playing');
      } else {
        video.pause();
        playBtn.classList.remove('is-playing');
      }
    });

    // Show play button again when video ends
    document.querySelectorAll('.video-container video').forEach(function(video) {
      video.addEventListener('ended', function() {
        var playBtn = this.closest('.video-container').querySelector('.video-play-btn');
        if (playBtn) playBtn.classList.remove('is-playing');
      });
    });
  }

  // ── Mobile Menu ──
  function initMobileMenu() {
    document.addEventListener('click', function(e) {
      var hamburger = e.target.closest('[data-mobile-menu-toggle]');
      if (hamburger) {
        var menu = document.querySelector('[data-mobile-menu]');
        if (menu) {
          var isOpen = menu.classList.toggle('is-open');
          document.documentElement.classList.toggle('scroll-lock', isOpen);
          hamburger.setAttribute('aria-expanded', isOpen);
        }
        return;
      }

      // Close menu on link click
      var menuLink = e.target.closest('[data-mobile-menu] a, [data-mobile-menu] button:not([data-mobile-menu-toggle])');
      if (menuLink) {
        var openMenu = document.querySelector('[data-mobile-menu].is-open');
        if (openMenu) {
          openMenu.classList.remove('is-open');
          document.documentElement.classList.remove('scroll-lock');
          var toggle = document.querySelector('[data-mobile-menu-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  // ── Header scroll effect ──
  function initHeaderScroll() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var lastScroll = 0;
    function onScroll() {
      var scrollY = window.scrollY;
      if (scrollY > 20) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
      lastScroll = scrollY;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ── Initialize all ──
  function init() {
    initStaggerAnimations();
    initSectionReveal();
    initScrollAnimations();
    initParallax();
    initSmoothScroll();
    initCounterAnimation();
    initLanguageSelector();
    initPreloader();
    initScienceModal();
    initVideoToggle();
    initMobileMenu();
    initHeaderScroll();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', function() {
    initStaggerAnimations();
    initSectionReveal();
    initScrollAnimations();
    initParallax();
    initSmoothScroll();
    initCounterAnimation();
    initVideoToggle();
    initHeaderScroll();
  });
})();
